import { useState } from "react";
import {
  ActionIcon,
  Collapse,
  Group,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import IconChevronDown from "@tabler/icons-react/dist/esm/icons/IconChevronDown.mjs";
import IconTrash from "@tabler/icons-react/dist/esm/icons/IconTrash.mjs";

import EntityField from "./EntityField";
import ConnectorAnchor from "../../connectors/ConnectorAnchor";
import { useEditEntityNameMutation } from "../../hooks/mutations";
import { DeleteConfirmModal } from "../../../DeleteConfirmationModal";
import classes from "./EntityAccordion.module.scss";

/**
 * Visual accordion for one entity in the entity tree: name (editable for
 * non-top-level entities), an optional expand chevron, and the scrollable
 * list of draggable fields. Wires up the two connection-point anchors
 * (leftId, rightId) so the parent Entity wrapper can draw connectors.
 */
export default function EntityAccordion({
  ruleId,
  queryId,
  data,
  name,
  open,
  hasConnections,
  startPointId,
  leftId,
  rightId,
  allowRemove,
  isTopLevel,
  diplayedEntityId,
  canEdit = true,
  onOpen,
  onClose,
  onRemove,
}) {
  const [editMode, setEditMode] = useState(false);
  const [deleteOpened, setDeleteOpened] = useState(false);
  const displayedName = name || data?.name;

  const { mutate: editEntityName } = useEditEntityNameMutation({
    ruleId,
    queryId,
  });

  const onNameSave = (value) => {
    if (!value || value === displayedName) {
      setEditMode(false);
      return;
    }
    editEntityName({ id: diplayedEntityId, name: value });
    setEditMode(false);
  };

  return (
    <div className={classes.accordion}>
      {/* Invisible anchors. Left anchor declares its incoming edge via from={startPointId}. */}
      {startPointId && (
        <ConnectorAnchor id={leftId} from={startPointId} side="left" />
      )}
      {open && hasConnections && (
        <ConnectorAnchor id={rightId} side="right" />
      )}

      <section className={classes.section}>
        <div
          className={classes.header}
          onClick={() => {
            if (diplayedEntityId && canEdit) setEditMode(true);
          }}
        >
          {editMode ? (
            <TextInput
              defaultValue={displayedName}
              autoFocus
              variant="unstyled"
              onBlur={(ev) => onNameSave(ev.currentTarget.value)}
              onKeyDown={(ev) => {
                if (ev.key === "Enter") ev.currentTarget.blur();
                if (ev.key === "Escape") setEditMode(false);
              }}
            />
          ) : (
            <Text size="sm" fw={500}>
              {displayedName}
            </Text>
          )}

          <Group gap={2} wrap="nowrap" className={classes.headerControls}>
            {!isTopLevel && canEdit && (
              <Tooltip label={open ? "Collapse" : "Expand"} withArrow>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label={open ? "Collapse entity" : "Expand entity"}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    if (open) {
                      onClose?.();
                    } else {
                      onOpen?.();
                    }
                  }}
                  className={`${classes.chevron} ${open ? classes.chevronOpen : ""}`}
                >
                  <IconChevronDown size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            {allowRemove && canEdit && (
              <Tooltip label="Remove entity" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label="Remove entity"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setDeleteOpened(true);
                  }}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </div>

        {/* Fields reveal only when expanded — matches the original
            max-height 35px→400px behaviour; a collapsed relation shows
            just its name header. (Top-level entities are always open.) */}
        <Collapse in={open}>
          <ScrollArea.Autosize mah={150} type="auto" scrollbars="y">
            <Stack gap={1} p={2}>
              {data?.fields?.map((field) => (
                <EntityField
                  key={field.id}
                  queryId={queryId}
                  entityField={field}
                  displayedEntityId={diplayedEntityId}
                  canEdit={canEdit}
                />
              ))}
            </Stack>
          </ScrollArea.Autosize>
        </Collapse>
      </section>

      <DeleteConfirmModal
        opened={deleteOpened}
        onClose={() => setDeleteOpened(false)}
        onDelete={() => {
          onRemove?.(diplayedEntityId);
          setDeleteOpened(false);
        }}
        label="Delete entity?"
      />
    </div>
  );
}
